import { Heading } from "@solomon-labs/design/components/ui";
import React, { useCallback, useMemo, useState } from "react";

import { StatusBadge } from "../../../components/StatusBadge/StatusBadge";
import { FormActions } from "../../../components/ui/form-actions";
import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import { ProgramActiveHeader } from "./ProgramActiveHeader";
import { ProgramConfiguration } from "./ProgramConfiguration";
import { ProgramKeyStats } from "./ProgramKeyStats";
import { ProgramPolicies } from "./ProgramPolicies";
import { ProgramProgress } from "./ProgramProgress";
import { QualifiedUsers } from "./QualifiedUsers";
import type { ProgramConfig } from "./types";
import { type SimScriptStep, useSimScript } from "./useSimScript";
import {
    VIEWING_BUDGET_METER,
    VIEWING_CONFIG,
    VIEWING_KEY_STATS,
    VIEWING_NEXT_DISTRIBUTION,
    VIEWING_PARTICIPANTS,
    VIEWING_POLICIES,
    VIEWING_POLICY_VALUES,
    VIEWING_PROGRAM_NAME,
    VIEWING_TAGS,
    VIEWING_TIMELINE_METER
} from "./viewingFixtures";

/**
 * Auto-playing, CURSOR-DRIVEN driver for the bare **program activation** doc
 * block — the transition from a *filled-out Draft* into the created program's
 * read view as it goes **Pending → Active**.
 *
 * A SIXTH "+New Program" doc block, sitting after "Viewing Program". Where the
 * first four blocks auto-play a *populate* choreography and "Viewing Program"
 * shows a settled read display, this block demonstrates the **activation
 * moment**: the draft is already complete (no field-populate — the reused
 * hoisted Views render from static filled fixtures), the scripted cursor
 * travels to the **"Add Program"** CTA and clicks it, and that click swaps the
 * whole block to the program's read view with the status reading **"Pending"**.
 * After a short wait the status flips **Pending → Active**. Then it loops back
 * to the filled Draft.
 *
 * Three phases (one looping cycle):
 *   1. **Draft (filled)** — draft header (program name + a **"Draft"**
 *      StatusBadge) + the Configuration / Policies sections shown
 *      already filled with the same values the read view uses, ending with the
 *      real **"Add Program"** {@link FormActions} CTA. A bare section (no page
 *      chrome).
 *   2. **Cursor click** — a visible {@link useSimCursor} travels to the Add
 *      Program CTA, hovers (forced hover on the CTA), and clicks (press pulse +
 *      forced press). The click triggers the swap.
 *   3. **Pending → Active** — swap to the read view (reusing "Viewing
 *      Program"'s composition: header + key stats + meters + read-only Config
 *      summary + read-only Policies + Qualified Users), seeded from the SAME
 *      values, with the StatusBadge reading **"Pending"** (amber). Hold, then
 *      flip the StatusBadge **Pending → Active** (green). Hold, then loop.
 *
 * All data is self-contained fixtures (the draft is derived from
 * {@link viewingFixtures} so the read view matches the draft the user "filled
 * in"); no wallet / RPC / orgApiFetch / router. Reduced-motion collapses to
 * the Active read-view end-state with no cursor (the `useSimScript` synchronous
 * pass runs every beat, landing on the final phase).
 *
 * Drop-in for external (docs/wiki) consumers:
 *
 *   import "@solomon-apps/shared/sim/index.css";
 *   import { ProgramActivationSim } from "@solomon-apps/shared/sim";
 *   <ProgramActivationSim />
 *
 * No `SimModeProvider` — the activation is mocked as local phase state; no
 * simulated transaction / scenario branch.
 */

/** No-op — the reused create-mode Views require setters/mutators, but this sim
 *  never mutates through them (the draft is static-filled, the read view is
 *  read-only). All are wired to this. */
const NOOP = (): void => {};

/** The two lifecycle phases this block cycles through: the filled draft, then
 *  the read view (which itself flips its StatusBadge pending → active). */
type Phase = "draft" | "view";

/** Sim/forthcoming activation status the read view shows. `pending` is the
 *  brief post-"Add Program" state before the program goes live; `active` is
 *  the settled live program. (Real backend support for a distinct "Pending"
 *  program status is TBD — see StatusBadge.) */
type ActivationStatus = "pending" | "active";

/** StatusBadge fixtures per activation status — the label + dot state + label
 *  color the read-view header renders. `pending` = amber (warning tone),
 *  `active` = green (success tone). */
const ACTIVATION_BADGE: Record<
    ActivationStatus,
    { label: string; state: "pending" | "success"; labelClassName: string }
> = {
    pending: {
        label: "Pending",
        state: "pending",
        labelClassName: "text-warning-base"
    },
    active: {
        label: "Active",
        state: "success",
        labelClassName: "text-success-base"
    }
};

/**
 * The filled draft, derived from the viewing fixtures so the read view the
 * activation swaps to matches the draft the user "filled in." A `ProgramConfig`
 * with `status: "in_progress"` so `ProgramConfiguration` renders its editable
 * (draft) form — pre-filled with the viewing values.
 */
const DRAFT_CONFIG: ProgramConfig = {
    ...VIEWING_CONFIG,
    status: "in_progress"
};

// ── Phase timing (ms) ───────────────────────────────────────────────────
/** Lead before the CTA hover — the cursor starts travelling here so it arrives
 *  as the hover paints. Pinned to the move `durationMs` so arrival == hover. */
const TRAVEL_MS = 900;
/** How long the cursor rests on the CTA painting its forced HOVER before the
 *  "click." */
const HOVER_HOLD_MS = 620;
/** How long the CTA holds its forced PRESS visual (the click) before the swap
 *  to the read view. */
const PRESS_HOLD_MS = 320;
/** How long the read view holds on **Pending** before flipping to Active. */
const PENDING_HOLD_MS = 2500;
/** How long the read view holds on **Active** before the loop pause. */
const ACTIVE_HOLD_MS = 2200;

export interface ProgramActivationSimProps extends FramableProps {
    /**
     * Pause (ms) after the Active read view settles before the block replays
     * from the filled Draft. Defaults to 3500. Set to `-1` to play once and
     * stop (landing on the Active read view).
     */
    loopPauseMs?: number;
}

function ProgramActivationSimView({
    loopPauseMs = 3500
}: ProgramActivationSimProps = {}): React.ReactElement {
    // Which lifecycle frame is showing: the filled draft, or the read view.
    const [phase, setPhase] = useState<Phase>("draft");
    // The read view's activation status (only meaningful in the "view" phase).
    const [status, setStatus] = useState<ActivationStatus>("pending");
    // The forced visual on the "Add Program" CTA, or null.
    const [ctaDemo, setCtaDemo] = useState<"hover" | "press" | null>(null);

    const cursor = useSimCursor();
    const { moveTo: moveCursor, click: clickCursor, hide: hideCursor } = cursor;

    const reset = useCallback((): void => {
        setPhase("draft");
        setStatus("pending");
        setCtaDemo(null);
        hideCursor();
    }, [hideCursor]);

    // The scripted beats:
    //   travel to the Add Program CTA → hover (forced, pinned so arrival ==
    //   hover) → press (click pulse + forced press) → SWAP to the read view on
    //   "Pending" (drop the cursor — the read view is a settled frame) → hold →
    //   flip Pending → Active → hold → loop back to the filled draft.
    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            {
                after: TRAVEL_MS,
                label: "add-program:move",
                // Pin the travel to the hover's lead so the pointer arrives
                // exactly as the forced hover paints — no distance-derived lag.
                run: (): void =>
                    moveCursor("activate-add-program", {
                        durationMs: TRAVEL_MS
                    })
            },
            {
                // Paint the CTA hover the instant travel completes (`after`
                // === TRAVEL_MS) so it lands with the pointer's arrival.
                after: TRAVEL_MS,
                label: "add-program:hover",
                run: (): void => setCtaDemo("hover")
            },
            {
                after: HOVER_HOLD_MS,
                label: "add-program:press",
                run: (): void => {
                    clickCursor();
                    setCtaDemo("press");
                }
            },
            {
                // The click's effect: swap to the read view on "Pending" and
                // fade the cursor out (the read view is a settled read frame,
                // no pointer).
                after: PRESS_HOLD_MS,
                label: "swap:pending",
                run: (): void => {
                    setCtaDemo(null);
                    setStatus("pending");
                    setPhase("view");
                    hideCursor();
                }
            },
            {
                // Flip the StatusBadge Pending → Active.
                after: PENDING_HOLD_MS,
                label: "flip:active",
                run: (): void => setStatus("active")
            },
            // Hold on Active before the loop pause resets to the filled draft.
            {
                after: ACTIVE_HOLD_MS,
                label: "hold:active",
                run: NOOP
            }
        ],
        [moveCursor, clickCursor, hideCursor]
    );

    useSimScript({ reset, steps, loopPauseMs });

    return (
        <div ref={cursor.containerRef} className="relative">
            {phase === "draft" ? (
                <DraftFrame ctaDemo={ctaDemo} />
            ) : (
                <ReadFrame status={status} />
            )}
            {cursor.layer}
        </div>
    );
}

/**
 * The activation moment (filled Draft → "Add Program" → Pending → Active).
 * Add `framed` to wrap it in its own fixed-height {@link SimFrame} (the block
 * owns its max height); omit it to render bare.
 */
export const ProgramActivationSim = framable(
    ProgramActivationSimView,
    "ProgramActivationSim"
);

/* ── Phase 1 — the filled Draft ─────────────────────────────────────────── */

/** The filled-out create form: a draft header (name + "Draft" StatusBadge),
 *  the Configuration / Policies sections pre-filled with the read view's
 *  values, and the real "Add Program" FormActions CTA. */
function DraftFrame({
    ctaDemo
}: {
    ctaDemo: "hover" | "press" | null;
}): React.ReactElement {
    return (
        <div>
            {/* Draft header — mirrors the app's draft `ProgramHeader`: the
                program title (Heading, text-3xl) + a "Draft" StatusBadge
                (amber/pending dot + warning-toned label), matching the app's
                in_progress status treatment. */}
            <section className="py-4">
                <div className="px-4 flex items-center gap-8">
                    <Heading
                        title={VIEWING_PROGRAM_NAME}
                        titleVariant="h2"
                        tone="base"
                        titleClassName="text-3xl"
                    />
                    <StatusBadge
                        state="pending"
                        label="Draft"
                        labelClassName="text-warning-base"
                    />
                </div>
            </section>

            {/* Configuration — draft (editable) form, pre-filled. Static
                fixtures, so the setters are no-ops (this frame never mutates —
                the "Add Program" click drives the swap, not field edits). */}
            <ProgramConfiguration
                program={DRAFT_CONFIG}
                programType={DRAFT_CONFIG.programType ?? "fixed"}
                setProgramType={NOOP}
                budgetMode={DRAFT_CONFIG.budgetMode ?? "fixed"}
                setBudgetMode={NOOP}
                apyMode={(DRAFT_CONFIG.baseApy ?? 0) > 0 ? "base" : "earned"}
                setApyMode={NOOP}
                baseApy={DRAFT_CONFIG.baseApy ?? 0}
                setBaseApy={NOOP}
                userPct={DRAFT_CONFIG.userPct ?? 80}
                setUserPct={NOOP}
                periodDays={DRAFT_CONFIG.periodDays ?? 0}
                setPeriodDays={NOOP}
                settlementCycleLength={DRAFT_CONFIG.settlementCycleLength ?? 0}
                setSettlementCycleLength={NOOP}
                settlementCycleUnit={DRAFT_CONFIG.settlementCycleUnit ?? "days"}
                setSettlementCycleUnit={NOOP}
                budget={DRAFT_CONFIG.budget ?? 0}
                setBudget={NOOP}
            />

            {/* Policies — draft (editable) list with the two attached policies
                already added (they show under the "Added" label). Mutators are
                no-ops. */}
            <ProgramPolicies
                program={{ status: "in_progress" }}
                policies={VIEWING_POLICIES}
                policyValues={VIEWING_POLICY_VALUES}
                onAddPolicy={NOOP}
                onRemovePolicy={NOOP}
                onChangePolicyValue={NOOP}
            />

            {/* The create CTA — the real "Add Program" FormActions. Carries the
                sim cursor key + forced hover/press so the scripted pointer
                targets it; clicking it is what triggers the activation swap. */}
            <section className="mt-8 mb-10 px-4 flex items-center gap-3">
                <FormActions
                    size="lg"
                    primaryLabel="Add Program"
                    onPrimary={NOOP}
                    cancelLabel="Cancel"
                    onCancel={NOOP}
                    primaryCursorKey="activate-add-program"
                    primaryForceHover={ctaDemo === "hover"}
                    primaryForcePressed={ctaDemo === "press"}
                />
            </section>
        </div>
    );
}

/* ── Phase 3 — the read view (Pending → Active) ─────────────────────────── */

/** The program's read view — reuses "Viewing Program"'s composition, seeded
 *  from the SAME values as the draft, with the StatusBadge reading the given
 *  activation status (Pending amber → Active green). */
function ReadFrame({
    status
}: {
    status: ActivationStatus;
}): React.ReactElement {
    const badge = ACTIVATION_BADGE[status];
    return (
        <div>
            {/* Active-title header — title + StatusBadge (Pending → Active) +
                tag chips + the live Next Distribution countdown on the right. */}
            <ProgramActiveHeader
                name={VIEWING_PROGRAM_NAME}
                statusLabel={badge.label}
                statusState={badge.state}
                statusLabelClassName={badge.labelClassName}
                tags={VIEWING_TAGS}
                nextDistribution={VIEWING_NEXT_DISTRIBUTION}
            />

            {/* Key-stats row — held / accrued / projected / distributed /
                users. */}
            <ProgramKeyStats stats={VIEWING_KEY_STATS} />

            {/* Budget + timeline progress meters. */}
            <ProgramProgress
                budgetMeter={VIEWING_BUDGET_METER}
                timelineMeter={VIEWING_TIMELINE_METER}
            />

            {/* Read-only Configuration summary — the `!creation` path (status:
                "active"); the draft setters are no-ops. */}
            <ProgramConfiguration
                program={VIEWING_CONFIG}
                programType="fixed"
                setProgramType={NOOP}
                budgetMode="fixed"
                setBudgetMode={NOOP}
                apyMode={(VIEWING_CONFIG.baseApy ?? 0) > 0 ? "base" : "earned"}
                setApyMode={NOOP}
                baseApy={VIEWING_CONFIG.baseApy ?? 0}
                setBaseApy={NOOP}
                userPct={VIEWING_CONFIG.userPct ?? 80}
                setUserPct={NOOP}
                periodDays={VIEWING_CONFIG.periodDays ?? 0}
                setPeriodDays={NOOP}
                settlementCycleLength={
                    VIEWING_CONFIG.settlementCycleLength ?? 0
                }
                setSettlementCycleLength={NOOP}
                settlementCycleUnit={
                    VIEWING_CONFIG.settlementCycleUnit ?? "days"
                }
                setSettlementCycleUnit={NOOP}
                budget={VIEWING_CONFIG.budget ?? 0}
                setBudget={NOOP}
            />

            {/* Read-only Policies list — the read path (status: "active"); the
                mutators are no-ops. */}
            <ProgramPolicies
                program={{ status: "active" }}
                policies={VIEWING_POLICIES}
                policyValues={VIEWING_POLICY_VALUES}
                onAddPolicy={NOOP}
                onRemovePolicy={NOOP}
                onChangePolicyValue={NOOP}
            />

            {/* Qualified Users table. */}
            <QualifiedUsers participants={VIEWING_PARTICIPANTS} />
        </div>
    );
}
